import { jsPDF } from 'jspdf';

interface Player {
  id: string;
  firstName?: string;
  lastName?: string;
  user?: {
    firstName?: string;
    lastName?: string;
  };
  academyName?: string;
}

interface RankingStroke {
  code: string;
  courtPosition: string;
  halfSide: string;
  strokeType: string;
  strokeAction: string;
  shotType: string;
  placement: string;
  rating: number;
}

interface RankingEvaluationReport {
  player: Player;
  categoryId: string;
  categoryLabel: string;
  sport: string;
  evaluationDate: string;
  strokes: RankingStroke[];
  totalScore: number;
  maxScore: number;
  ratedStrokes: number;
  percentage: number;
  // Rated-only scores (optional for backward compatibility)
  ratedScore?: number;
  ratedMaxScore?: number;
  ratedPercentage?: number;
  // Coverage scores and percentages (optional for backward compatibility)
  backcourtScore?: number;
  backcourtMax?: number;
  backcourtPercentage?: number;
  midcourtScore?: number;
  midcourtMax?: number;
  midcourtPercentage?: number;
  frontcourtScore?: number;
  frontcourtMax?: number;
  frontcourtPercentage?: number;
  grade?: string;
  gradeDescription?: string;
  coachName?: string;
  submittedAt?: string;
}

// Helper function to get period label from date
const getPeriodLabel = (date: string): string => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = d.getMonth() + 1;
  
  if (month === 12) {
    return `Yearly ${year}`;
  } else if (month >= 1 && month <= 3) {
    return `Q1 ${year}`;
  } else if (month >= 4 && month <= 6) {
    return `Q2 ${year}`;
  } else if (month >= 7 && month <= 9) {
    return `Q3 ${year}`;
  } else if (month >= 10 && month <= 11) {
    return `Q4 ${year}`;
  }
  return `${year}`;
};

// Helper function to convert half court values to shorter format
const normalizeHalfCourt = (halfSide: string): string => {
  if (!halfSide) return 'N/A';
  const side = halfSide.toLowerCase().trim();
  if (side.includes('right service court') || side.includes('right side')) {
    return 'Right Side';
  }
  if (side.includes('left service court') || side.includes('left side')) {
    return 'Left Side';
  }
  return halfSide; // Return original if no match
};

export const generateRankingEvaluationReportPDF = async (
  report: RankingEvaluationReport
) => {
  try {
    if (!report || !report.player || !report.strokes) {
      throw new Error('Invalid report data provided');
    }
    
    if (!Array.isArray(report.strokes) || report.strokes.length === 0) {
      throw new Error('No stroke data available for report generation');
    }
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 10;
    const footerHeight = 15;
    const headerHeight = 20;
    let yPosition = headerHeight;
    const rowHeight = 6;
    const tableStartX = margin;

    // Header with branding
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Encycdata', margin, 8);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('EncycGames', margin, 13);

    // Report title
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text(
      `Ranking Evaluation Report - ${report.categoryLabel}`,
      pageWidth / 2,
      yPosition,
      { align: 'center' }
    );
    yPosition += 8;

    // Header Information
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    const playerName = `${report.player?.firstName || report.player?.user?.firstName || ''} ${report.player?.lastName || report.player?.user?.lastName || ''}`.trim();
    const academyName = report.player?.academyName || 'N/A';
    const coachName = report.coachName || 'N/A'; // Ensure coachName is always defined
    const reportDate = report.submittedAt 
      ? new Date(report.submittedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
      : new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    const evaluationDate = report.evaluationDate 
      ? new Date(report.evaluationDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
      : reportDate;
    const periodLabel = getPeriodLabel(report.evaluationDate || new Date().toISOString());

    doc.text(`Player: ${playerName}`, margin, yPosition);
    // Show "Evaluated By" instead of "Coach", and use "self" for self-evaluations
    const evaluatedBy = report.categoryId === 'self-evaluation' ? 'self' : (coachName || 'N/A');
    doc.text(`Evaluated By: ${evaluatedBy}`, pageWidth / 2, yPosition);
    yPosition += 5;
    doc.text(`Academy: ${academyName}`, margin, yPosition);
    doc.text(`Submission Date: ${reportDate}`, pageWidth / 2, yPosition);
    yPosition += 5;
    doc.text(`Evaluation Period: ${periodLabel}`, margin, yPosition);
    doc.text(`Sport: ${report.sport}`, pageWidth / 2, yPosition);
    yPosition += 8;

    // Rating Scale Reference Information (matching Performance Metrics)
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text('Rating Scale Reference (For Information Only):', margin, yPosition);
    yPosition += 6;
    
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    
    // Calculate the maximum width of level names for alignment
    const levelNames = [
      'Beginner (1-2):',
      'Intermediate (2-3):',
      'Advanced (3-4):',
      'Master (4-5):'
    ];
    doc.setFont('helvetica', 'bold');
    const maxLevelWidth = Math.max(...levelNames.map(name => doc.getTextWidth(name)));
    const definitionStartX = margin + 8 + maxLevelWidth + 5;
    
    // Beginner (1-2)
    doc.setFillColor(244, 67, 54);
    doc.rect(margin, yPosition - 2, 3, 3, 'F');
    doc.setFont('helvetica', 'bold');
    doc.text('Beginner (1-2):', margin + 8, yPosition);
    doc.setFont('helvetica', 'normal');
    doc.text('Learning basic rules and shots', definitionStartX, yPosition);
    yPosition += 5;
    
    // Intermediate (2-3)
    doc.setFillColor(255, 152, 0);
    doc.rect(margin, yPosition - 2, 3, 3, 'F');
    doc.setFont('helvetica', 'bold');
    doc.text('Intermediate (2-3):', margin + 8, yPosition);
    doc.setFont('helvetica', 'normal');
    doc.text('Can perform upto 5 consistent strokes but lacking accuracy and perfection', definitionStartX, yPosition);
    yPosition += 5;
    
    // Advanced (3-4)
    doc.setFillColor(33, 150, 243);
    doc.rect(margin, yPosition - 2, 3, 3, 'F');
    doc.setFont('helvetica', 'bold');
    doc.text('Advanced (3-4):', margin + 8, yPosition);
    doc.setFont('helvetica', 'normal');
    doc.text('Can play 15 or more consistent strokes and do long rallies with different shots', definitionStartX, yPosition);
    yPosition += 5;
    
    // Master (4-5)
    doc.setFillColor(76, 175, 80);
    doc.rect(margin, yPosition - 2, 3, 3, 'F');
    doc.setFont('helvetica', 'bold');
    doc.text('Master (4-5):', margin + 8, yPosition);
    doc.setFont('helvetica', 'normal');
    doc.text('Aggressive, Accurate and consistently performing in varying conditions', definitionStartX, yPosition);
    yPosition += 8;

    // Summary section
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Summary', margin, yPosition);
    yPosition += 6;

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    
    // Accumulated Score
    doc.setFont('helvetica', 'bold');
    doc.text('Accumulated Score:', margin, yPosition);
    doc.setFont('helvetica', 'normal');
    doc.text(`Total Score: ${report.totalScore} / ${report.maxScore}`, margin + 50, yPosition);
    doc.text(`Percentage: ${report.percentage.toFixed(1)}%`, pageWidth / 2, yPosition);
    yPosition += 5;
    
    // Rated-Only Score (if available)
    if (report.ratedScore !== undefined && report.ratedMaxScore !== undefined) {
      doc.setFont('helvetica', 'bold');
      doc.text('Rated Score (Rated Only):', margin, yPosition);
      doc.setFont('helvetica', 'normal');
      doc.text(`Score: ${report.ratedScore} / ${report.ratedMaxScore}`, margin + 50, yPosition);
      const ratedPct = report.ratedPercentage !== undefined ? report.ratedPercentage.toFixed(1) : 
                       (report.ratedMaxScore > 0 ? ((report.ratedScore / report.ratedMaxScore) * 100).toFixed(1) : '0.0');
      doc.text(`Percentage: ${ratedPct}%`, pageWidth / 2, yPosition);
      yPosition += 5;
    }
    
    doc.text(`Rated Strokes: ${report.ratedStrokes}`, margin, yPosition);
    if (report.grade) {
      doc.setFont('helvetica', 'bold');
      doc.text(`Grade: ${report.grade}`, pageWidth / 2, yPosition);
      if (report.gradeDescription) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        yPosition += 4;
        doc.text(`(${report.gradeDescription})`, pageWidth / 2, yPosition);
      }
    }
    yPosition += 5;
    
    // Coverage Percentages (if available)
    if (report.backcourtPercentage !== undefined || report.midcourtPercentage !== undefined || report.frontcourtPercentage !== undefined) {
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text('Court Coverage:', margin, yPosition);
      yPosition += 5;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      
      if (report.backcourtPercentage !== undefined) {
        doc.text(`Backcourt: ${report.backcourtPercentage.toFixed(1)}%`, margin, yPosition);
        if (report.backcourtScore !== undefined && report.backcourtMax !== undefined) {
          doc.text(`(${report.backcourtScore} / ${report.backcourtMax})`, margin + 50, yPosition);
        }
      }
      if (report.midcourtPercentage !== undefined) {
        doc.text(`Midcourt: ${report.midcourtPercentage.toFixed(1)}%`, pageWidth / 2, yPosition);
        if (report.midcourtScore !== undefined && report.midcourtMax !== undefined) {
          doc.text(`(${report.midcourtScore} / ${report.midcourtMax})`, pageWidth / 2 + 50, yPosition);
        }
      }
      yPosition += 4;
      if (report.frontcourtPercentage !== undefined) {
        doc.text(`Frontcourt: ${report.frontcourtPercentage.toFixed(1)}%`, margin, yPosition);
        if (report.frontcourtScore !== undefined && report.frontcourtMax !== undefined) {
          doc.text(`(${report.frontcourtScore} / ${report.frontcourtMax})`, margin + 50, yPosition);
        }
      }
      yPosition += 5;
    }
    
    yPosition += 3;

    // Table column widths
    // Reduced: courtPosition, strokeAction by 30%
    // Increased: shotType, placement by 40%
    // Reduced: rating by 20% (from 0.191 to 0.153)
    // Increased: strokeType by 20% (from 0.131 to 0.157)
    // Adjusted to ensure total = 1.0
    const colWidths = {
      courtPosition: (pageWidth - 2 * margin) * 0.105,  // 0.15 * 0.7 = 0.105
      halfCourt: (pageWidth - 2 * margin) * 0.10,       // Keep same
      strokeType: (pageWidth - 2 * margin) * 0.169,     // 0.131 * 1.2 = 0.157, adjusted to 0.169 for total = 1.0
      strokeAction: (pageWidth - 2 * margin) * 0.105,    // 0.15 * 0.7 = 0.105
      shotType: (pageWidth - 2 * margin) * 0.168,        // 0.12 * 1.4 = 0.168
      placement: (pageWidth - 2 * margin) * 0.20,        // Keep same
      rating: (pageWidth - 2 * margin) * 0.153,         // 0.191 * 0.8 = 0.153
    };

    const drawTableRow = (x: number, y: number, lineWidth: number = 0.2) => {
      doc.setLineWidth(lineWidth);
      let currentX = x;
      const lineY = y + rowHeight;
      const tableEndX = x + (pageWidth - 2 * margin);

      doc.line(currentX, y, currentX, lineY);
      currentX += colWidths.courtPosition;
      doc.line(currentX, y, currentX, lineY);
      currentX += colWidths.halfCourt;
      doc.line(currentX, y, currentX, lineY);
      currentX += colWidths.strokeType;
      doc.line(currentX, y, currentX, lineY);
      currentX += colWidths.strokeAction;
      doc.line(currentX, y, currentX, lineY);
      currentX += colWidths.shotType;
      doc.line(currentX, y, currentX, lineY);
      currentX += colWidths.placement;
      doc.line(currentX, y, currentX, lineY);
      currentX += colWidths.rating;
      doc.line(currentX, y, currentX, lineY);
      doc.line(x, lineY, tableEndX, lineY);
    };

    const drawTableHeader = (y: number) => {
      const headerY = y;

      doc.setFillColor(245, 245, 245);
      doc.rect(tableStartX, headerY, pageWidth - 2 * margin, rowHeight, 'F');

      doc.setLineWidth(0.5);
      doc.line(tableStartX, headerY, tableStartX + (pageWidth - 2 * margin), headerY);

      drawTableRow(tableStartX, headerY, 0.3);

      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      let textX = tableStartX;
      doc.text('Court Position', textX + colWidths.courtPosition / 2, headerY + 4, { align: 'center' });
      textX += colWidths.courtPosition;
      doc.text('Half Court', textX + colWidths.halfCourt / 2, headerY + 4, { align: 'center' });
      textX += colWidths.halfCourt;
      doc.text('Stroke Type', textX + colWidths.strokeType / 2, headerY + 4, { align: 'center' });
      textX += colWidths.strokeType;
      doc.text('Stroke Action', textX + colWidths.strokeAction / 2, headerY + 4, { align: 'center' });
      textX += colWidths.strokeAction;
      doc.text('Shot Type', textX + colWidths.shotType / 2, headerY + 4, { align: 'center' });
      textX += colWidths.shotType;
      doc.text('Placement', textX + colWidths.placement / 2, headerY + 4, { align: 'center' });
      textX += colWidths.placement;
      doc.text('Rating', textX + colWidths.rating / 2, headerY + 4, { align: 'center' });
    };

    // Helper function to get court position color
    const getCourtPositionColor = (courtPosition: string): number[] | null => {
      if (!courtPosition) return null;
      const pos = courtPosition.toLowerCase().trim();
      if (pos.includes('backcourt') || pos.includes('back court')) {
        return [232, 245, 232]; // Light green for backcourt
      }
      if (pos.includes('midcourt') || pos.includes('mid court')) {
        return [227, 242, 253]; // Light blue for midcourt
      }
      if (pos.includes('frontcourt') || pos.includes('front court')) {
        return [255, 243, 224]; // Light orange for frontcourt
      }
      return null;
    };

    const drawTableDataRow = (stroke: RankingStroke, y: number) => {
      // Apply background color based on court position
      const bgColor = getCourtPositionColor(stroke.courtPosition);
      if (bgColor) {
        doc.setFillColor(bgColor[0], bgColor[1], bgColor[2]);
        doc.rect(tableStartX, y, pageWidth - 2 * margin, rowHeight, 'F');
      }
      
      // Draw borders after background to ensure they're visible
      drawTableRow(tableStartX, y, 0.2);

      doc.setFontSize(7);
      doc.setFont('helvetica', 'normal');
      let textX = tableStartX + 1;

      const courtPosition = doc.splitTextToSize(stroke.courtPosition || 'N/A', colWidths.courtPosition - 2);
      doc.text(courtPosition, textX, y + 4);
      textX += colWidths.courtPosition;

      const normalizedHalfCourt = normalizeHalfCourt(stroke.halfSide || 'N/A');
      const halfCourt = doc.splitTextToSize(normalizedHalfCourt, colWidths.halfCourt - 2);
      doc.text(halfCourt, textX, y + 4);
      textX += colWidths.halfCourt;

      const strokeType = doc.splitTextToSize(stroke.strokeType || 'N/A', colWidths.strokeType - 2);
      doc.text(strokeType, textX, y + 4);
      textX += colWidths.strokeType;

      const strokeAction = doc.splitTextToSize(stroke.strokeAction || 'N/A', colWidths.strokeAction - 2);
      doc.text(strokeAction, textX, y + 4);
      textX += colWidths.strokeAction;

      const shotType = doc.splitTextToSize(stroke.shotType || 'N/A', colWidths.shotType - 2);
      doc.text(shotType, textX, y + 4);
      textX += colWidths.shotType;

      const placement = doc.splitTextToSize(stroke.placement || 'N/A', colWidths.placement - 2);
      doc.text(placement, textX, y + 4);
      textX += colWidths.placement;

      // Rating stars - positioned within the rating column
      // Move content 5 spaces to the right from the start of rating column to fit properly and avoid left border
      const rating = stroke.rating || 0;
      const starSpacing = 2.8; // Reduced from 3.5 to prevent overlap
      const starSize = 1.0; // Slightly smaller stars
      // Position stars 5 spaces from the left edge of the rating column to ensure they fit within the column
      const startX = textX + 5;
      for (let i = 0; i < 5; i++) {
        if (i < rating) {
          doc.setFillColor(255, 193, 7); // Gold color for filled stars
          doc.circle(startX + i * starSpacing, y + 3, starSize, 'F');
        } else {
          doc.setLineWidth(0.1);
          doc.circle(startX + i * starSpacing, y + 3, starSize);
        }
      }
    };

    const addFooter = (doc: jsPDF, pageWidth: number, pageHeight: number, footerHeight: number) => {
      const footerY = pageHeight - footerHeight;

      // Draw footer line
      doc.setLineWidth(0.5);
      doc.line(10, footerY, pageWidth - 10, footerY);

      // Footer branding
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text('Encycdata', 10, footerY + 5);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text('EncycGames', 10, footerY + 10);

      // Page number
      const pageNum = doc.getCurrentPageInfo().pageNumber;
      const totalPages = doc.getNumberOfPages();
      doc.setFontSize(8);
      doc.text(`Page ${pageNum} of ${totalPages}`, pageWidth / 2, footerY + 7, { align: 'center' });

      // Date
      const footerDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
      doc.text(footerDate, pageWidth - 10, footerY + 7, { align: 'right' });
    };

    // Start table
    let tableStartY = yPosition;
    drawTableHeader(tableStartY);
    yPosition += rowHeight;

    // Draw strokes - maintain original order from API (no sorting)
    // This matches the manual form behavior
    report.strokes.forEach((stroke) => {
      if (yPosition + rowHeight > pageHeight - footerHeight) {
        addFooter(doc, pageWidth, pageHeight, footerHeight);
        doc.addPage();
        yPosition = headerHeight;
        tableStartY = yPosition;
        drawTableHeader(tableStartY);
        yPosition += rowHeight;
      }

      drawTableDataRow(stroke, yPosition);
      yPosition += rowHeight;
    });

    addFooter(doc, pageWidth, pageHeight, footerHeight);

    // Save PDF
    const fileName = `EncycGames_Ranking_Evaluation_${report.categoryLabel.replace(/\s+/g, '_')}_${playerName.replace(/\s+/g, '_')}_${new Date(report.evaluationDate || Date.now()).toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
  } catch (error) {
    console.error('Error generating ranking evaluation report PDF:', error);
    alert(`Error generating report: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

